import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpEventType,
  HttpParams,
  HttpStatusCode,
} from '@angular/common/http';
import { SKIP_GLOBAL_HTTP_ERROR_ALERT } from '@core/interceptors/http-error-context.token';
import { environment } from '@environment/environment';
import { Params } from '@angular/router';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import {
  AlternativeRequestDTO,
  AvailableProductsQueryParams,
  CarBrand,
  Counterparty,
  CounterpartySubdivision,
  CreatedRequestResult,
  CreateRequestDealDetailsData,
  CreateRequestsBatchPayload,
  DenyActTypeEnum,
  Installer,
  InstallerPerson,
  InstallersGroup,
  InstallZone,
  LogisticRequestRatingActionEnum,
  LogisticRequestRatingList,
  MainRequestFilesPayload,
  Product,
  RequestComment,
  RequestDealDetailsExtended,
  RequestDealDetailsWithInstaller,
  RequestDevice,
  RequestDeviceModelResponse,
  RequestDTO,
  SetReleaseCityManuallyResponse,
  RequestFile,
  RequestFileCategory,
  RequestFileCategoryGroup,
  RequestInstallerAssignmentClearPayload,
  RequestInstallerAssignmentPayload,
  RequestInstallerAssignmentResponse,
  RequestProduct,
  RequestRequiredFieldsSettings,
  RequestStatus,
  TemplatesOfActsPayload,
  TemporaryAccessCredentials,
  RolesProfile,
  CreateTemporaryAccessPayload,
  UpdateRequestObdDataPayload,
  UploadProgress,
} from '@core/types';
import { RequestModel } from '@core/models';
import { toRequestsSearchBody } from '@core/helpers';
import {
  CounterpartiesChecklistsCaption,
  FileGroupsWithFilesToSend,
} from '@features/requests/request-form/components/request-form-files/types';
import {
  DeviceInstallQualityCheckStatus,
  DeviceTamperStatus,
} from '@core/types/DeviceInstallQuality';
import { ComparingLogs, RequestLog } from '@core/types/RequestLog';
import {
  CreateRestrictedRequestCommentDto,
  LogisticRequestListResponse,
  LogisticRequestExtraField,
  LogisticRequestType,
  RestrictedRequestCommentDto,
  RestrictedRequestGuestLinkDto,
  RestrictedRequestGuestLinkRevokeDto,
  RestrictedRequestViewDto,
  UpdateLogisticRequestReleaseDateRequest,
  UpdateLogisticRequestReleaseDateResponse,
  LogisticGetDateTimeTransferReasonsResponse,
  RequestInstallerAssignmentInstaller,
  RequestInstallerAssignmentInstallersQuery,
} from '@outkeep/shared';

const { apiUrl, api2Url } = environment;

@Injectable({
  providedIn: 'root',
})
export class RequestsApiService {
  private readonly httpClient = inject(HttpClient);

  /**
   * Список заявок. Фильтры всегда уходят телом запроса: набор значений
   * массового фильтра не помещается в строку запроса, а два транспорта для
   * одного запроса усложняют сопровождение.
   */
  getAllRequests$(
    params: Params,
  ): Observable<LogisticRequestListResponse<RequestDTO>> {
    return this.httpClient.post<LogisticRequestListResponse<RequestDTO>>(
      `${api2Url}/logistic/requests`,
      toRequestsSearchBody(params),
    );
  }

  getAlternativeRequests$(): Observable<AlternativeRequestDTO[]> {
    return this.httpClient.get<AlternativeRequestDTO[]>(
      `${api2Url}/logistic/alternativeRequests`,
    );
  }

  getAvailableRequestTypes$(): Observable<LogisticRequestType[]> {
    return this.httpClient.get<LogisticRequestType[]>(
      `${api2Url}/logistic/requestTypes/available`,
    );
  }

  getRequestExtraFields$(): Observable<LogisticRequestExtraField[]> {
    return this.httpClient.get<LogisticRequestExtraField[]>(
      `${api2Url}/logistic/extraFields`,
    );
  }

  getRequest$(requestNumber: string): Observable<RequestDTO> {
    return this.httpClient.get<RequestDTO>(
      `${api2Url}/logistic/request/${requestNumber}`,
    );
  }

  getRestrictedRequest$(
    requestNumber: string,
  ): Observable<RestrictedRequestViewDto> {
    return this.httpClient.get<RestrictedRequestViewDto>(
      `${api2Url}/logistic/restricted-request/${requestNumber}`,
    );
  }

  createRestrictedRequestComment$(
    requestNumber: string,
    body: CreateRestrictedRequestCommentDto,
  ): Observable<RestrictedRequestCommentDto> {
    return this.httpClient.post<RestrictedRequestCommentDto>(
      `${api2Url}/logistic/restricted-request/${requestNumber}/comments`,
      body,
    );
  }

  createRestrictedRequestGuestLink$(
    requestNumber: string,
  ): Observable<RestrictedRequestGuestLinkDto> {
    return this.httpClient.post<RestrictedRequestGuestLinkDto>(
      `${api2Url}/logistic/restricted-request-links/${requestNumber}`,
      {},
    );
  }

  revokeRestrictedRequestGuestLinks$(
    requestNumber: string,
  ): Observable<RestrictedRequestGuestLinkRevokeDto> {
    return this.httpClient.delete<RestrictedRequestGuestLinkRevokeDto>(
      `${api2Url}/logistic/restricted-request-links/${requestNumber}`,
    );
  }

  getRestrictedRequestByGuestToken$(
    token: string,
  ): Observable<RestrictedRequestViewDto> {
    return this.httpClient.get<RestrictedRequestViewDto>(
      `${api2Url}/logistic/request-guest-view/${token}`,
    );
  }

  getTemporaryAccessRolesProfiles$(): Observable<RolesProfile[]> {
    return this.httpClient.get<RolesProfile[]>(
      `${apiUrl}/users/rolesProfiles/temporaryAccess`,
    );
  }

  createTemporaryAccess$(
    body: CreateTemporaryAccessPayload,
  ): Observable<TemporaryAccessCredentials> {
    return this.httpClient.post<TemporaryAccessCredentials>(
      `${api2Url}/users/temporaryAccess`,
      body,
    );
  }

  createRestrictedRequestGuestComment$(
    token: string,
    body: CreateRestrictedRequestCommentDto,
  ): Observable<RestrictedRequestCommentDto> {
    return this.httpClient.post<RestrictedRequestCommentDto>(
      `${api2Url}/logistic/request-guest-view/${token}/comments`,
      body,
    );
  }

  getCantSelectExecutorStatus$(): Observable<boolean> {
    return this.httpClient
      .get(`${apiUrl}/logistic/getCantSelectExecutor`)
      .pipe(map((counterparty: any) => !!counterparty.cantSelectExecutor));
  }

  getAvailableContractors$(
    counterpartyId: number | null = null,
  ): Observable<Counterparty[]> {
    return this.httpClient.get<Counterparty[]>(
      `${api2Url}/logistic/availableContractors/${counterpartyId}`,
    );
  }

  getContractorSubdivisions$(
    contractorId: number | null = null,
  ): Observable<CounterpartySubdivision[]> {
    return this.httpClient.get<CounterpartySubdivision[]>(
      `${api2Url}/registry/divisions/findforcounterparty/${contractorId}`,
    );
  }

  setSubdivisionIdManually$(
    requestId: RequestDTO['id'],
    subdivisionId: number,
  ): Observable<SetReleaseCityManuallyResponse> {
    return this.httpClient.put<SetReleaseCityManuallyResponse>(
      `${api2Url}/logistic/request/${requestId}/releaseCity`,
      {
        subdivisionId,
      },
    );
  }

  getZonesForClientAndContractor$(
    clientId: number | null = null,
    contractorId: number | null = null,
    contractorSubdivisionId: number | null = null,
  ): Observable<InstallZone[]> {
    return this.httpClient.get<InstallZone[]>(
      `${apiUrl}/logistic/installZones/getZonesForClientAndContractor/${clientId}/${contractorId}/${contractorSubdivisionId}`,
    );
  }

  getInstallersForClientAndContractor$(
    clientId: number | null = null,
    contractorId: number | null = null,
    contractorSubdivisionId: number | null = null,
  ): Observable<Installer[]> {
    return this.httpClient.get<Installer[]>(
      `${apiUrl}/logistic/installersforclientandcontractor/${clientId}/${contractorId}/${contractorSubdivisionId}`,
    );
  }

  getInstallers$(): Observable<InstallerPerson[]> {
    return this.httpClient
      .get<InstallerPerson[]>(`${api2Url}/logistic/installers`)
      .pipe(
        map(installers =>
          installers.map(installer => {
            const fullName = `${installer.lastName} ${installer.firstName} ${installer.patronymic}`;

            return { ...installer, fullName };
          }),
        ),
      );
  }

  getInstallersGroups(): Observable<InstallersGroup[]> {
    return this.httpClient.post<InstallersGroup[]>(
      `${api2Url}/logistic/getMyInstallersGroups`,
      {},
    );
  }

  setInstallerToRequest$(
    requestId: RequestDTO['id'],
    installerId: number,
  ): Observable<any> {
    return this.httpClient.post(
      `${apiUrl}/logistic/request/${requestId}/installer`,
      {
        installerId,
      },
    );
  }

  /**
   * Специалисты для назначения на заявку — из того же источника, что и проверка
   * при сохранении (как в реестре ТМЛ), в отличие от legacy installersforclientandcontractor.
   */
  getAssignmentInstallers$(
    query: RequestInstallerAssignmentInstallersQuery,
  ): Observable<RequestInstallerAssignmentInstaller[]> {
    return this.httpClient.get<RequestInstallerAssignmentInstaller[]>(
      `${api2Url}/logistic/request/installer-assignment/installers`,
      {
        params: new HttpParams({
          fromObject: {
            contractorCounterpartyId: query.contractorCounterpartyId,
            contractorCounterpartySubdivisionId:
              query.contractorCounterpartySubdivisionId,
          },
        }),
      },
    );
  }

  assignInstallerToRequests$(
    payload: RequestInstallerAssignmentPayload,
  ): Observable<RequestInstallerAssignmentResponse> {
    // Ошибку показывает сама форма назначения — без глобального алерта она дублируется
    return this.httpClient.patch<RequestInstallerAssignmentResponse>(
      `${api2Url}/logistic/request/installer-assignment`,
      payload,
      { context: this.skipGlobalErrorAlertContext() },
    );
  }

  clearInstallerFromRequests$(
    payload: RequestInstallerAssignmentClearPayload,
  ): Observable<RequestInstallerAssignmentResponse> {
    return this.httpClient.delete<RequestInstallerAssignmentResponse>(
      `${api2Url}/logistic/request/installer-assignment`,
      {
        body: payload,
        context: this.skipGlobalErrorAlertContext(),
      },
    );
  }

  private skipGlobalErrorAlertContext(): HttpContext {
    return new HttpContext().set(SKIP_GLOBAL_HTTP_ERROR_ALERT, true);
  }

  updateRequestsReleaseDate$(
    payload: UpdateLogisticRequestReleaseDateRequest,
  ): Observable<UpdateLogisticRequestReleaseDateResponse> {
    return this.httpClient.patch<UpdateLogisticRequestReleaseDateResponse>(
      `${api2Url}/logistic/request/release-date`,
      payload,
    );
  }

  getDateTimeTransferReasons$(): Observable<LogisticGetDateTimeTransferReasonsResponse> {
    return this.httpClient.get<LogisticGetDateTimeTransferReasonsResponse>(
      `${api2Url}/logistic/date-time-transfer-reasons`,
    );
  }

  getAllRequestsCounterparties$(): Observable<Counterparty[]> {
    return this.httpClient.get<Counterparty[]>(
      `${api2Url}/logistic/request-counterparties`,
      {
        headers: { 'Cache-Control': 'no-cache' },
      },
    );
  }

  getMyRequestCounterparties$(): Observable<Counterparty[]> {
    return this.httpClient.get<Counterparty[]>(
      `${api2Url}/logistic/request-counterparties/my`,
      {
        headers: { 'Cache-Control': 'no-cache' },
      },
    );
  }

  getRequestStatuses$(): Observable<RequestStatus[]> {
    return this.httpClient.get<RequestStatus[]>(
      `${api2Url}/logistic/request-statuses`,
    );
  }

  changeRequestsStatus$(
    requestsIds: RequestDTO['id'][],
    statusId: number,
    additionalData: {
      cancelReason?: string;
      requestToEditReason?: string;
    } = {},
    force = false,
    isGroupAction = false,
  ): Observable<RequestModel[]> {
    return this.httpClient
      .put<RequestDTO[]>(`${api2Url}/logistic/request-statuses`, {
        statusId,
        requestsIds,
        additionalData,
        force,
        isGroupAction,
      })
      .pipe(
        map(requests => requests.map(request => new RequestModel(request))),
      );
  }

  createRequestComment$(
    requestId: RequestDTO['id'],
    text: string,
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${api2Url}/logistic/request/${requestId}/comments`,
      {
        text,
      },
    );
  }

  deleteRequestComment$(
    requestId: RequestDTO['id'],
    commentId: RequestComment['id'],
  ): Observable<void> {
    return this.httpClient.delete<void>(
      `${api2Url}/logistic/request/comments/${commentId}`,
      {
        params: { requestId },
      },
    );
  }

  setRequestCancelReason$(
    requestId: RequestDTO['id'],
    reason: string,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/request/${requestId}/setCancelReason`,
      {
        reason,
      },
    );
  }

  getRequestDealDetails$(
    requestId: RequestDTO['id'],
    installerId: number | null,
  ): Observable<RequestDealDetailsExtended> {
    return this.httpClient.get<RequestDealDetailsExtended>(
      `${api2Url}/logistic/request/${requestId}/dealDetails?installerId=${installerId}`,
    );
  }

  getRequestsDealDetails(requestsIds: RequestDTO['id'][]) {
    return this.httpClient.get<RequestDealDetailsWithInstaller[]>(
      `${api2Url}/logistic/requests/dealDetails?requestsIds=${requestsIds.join(',')}`,
    );
  }

  createRequestDealDetails$(
    requestId: RequestDTO['id'],
    createData: CreateRequestDealDetailsData,
  ): Observable<CreateRequestDealDetailsData> {
    return this.httpClient.post<CreateRequestDealDetailsData>(
      `${api2Url}/logistic/request/${requestId}/dealDetails`,
      createData,
    );
  }

  getCarBrands$(): Observable<CarBrand[]> {
    return this.httpClient.get<CarBrand[]>(
      `${api2Url}/registry/carBrands?limit=10000`,
    );
  }

  updateRequestObdInfo$(
    requestId: RequestDTO['id'],
    obdData: UpdateRequestObdDataPayload,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/request/${requestId}/obdStatus`,
      obdData,
    );
  }

  updateInspectionResult(
    requestId: RequestDTO['id'],
    initialInspectionResult: RequestDTO['inspectionResult'],
    updatedInspectionResult: RequestDTO['inspectionResult'],
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${api2Url}/logistic/request/${requestId}/inspectionResult`,
      { initialInspectionResult, updatedInspectionResult },
    );
  }

  setActualReleaseDate$(
    requestId: RequestDTO['id'],
    actualReleaseDate: string,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/request/${requestId}/setActualReleaseDate`,
      { actualReleaseDate },
      { responseType: 'text' as any },
    );
  }

  resetActualReleaseDate$(requestId: RequestDTO['id']): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/request/${requestId}/resetActualReleaseDate`,
      {},
      { responseType: 'text' as any },
    );
  }

  setActualDeliveryAddress$(
    requestId: RequestDTO['id'],
    actualDeliveryAddress: string,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${api2Url}/logistic/request/${requestId}/changeActualDelAddress`,
      { actualDeliveryAddress },
      { responseType: 'text' as any },
    );
  }

  resetActualDeliveryAddress$(requestId: RequestDTO['id']): Observable<void> {
    return this.httpClient.put<void>(
      `${api2Url}/logistic/request/${requestId}/resetActualDelAddress`,
      {},
      { responseType: 'text' as any },
    );
  }

  // #region files

  getRequestFiles$(requestId: RequestDTO['id']): Observable<RequestFile[]> {
    return this.httpClient.get<RequestFile[]>(
      `${api2Url}/logistic/request/files/${requestId}`,
    );
  }

  getRequestFileCategories$(
    counterpartyIds: Counterparty['id'][],
  ): Observable<RequestFileCategory[]> {
    return this.httpClient
      .get<{
        categories: RequestFileCategory[];
      }>(`${api2Url}/logistic/requests/fileCategories`, {
        params: {
          counterpartyId: counterpartyIds,
          sort: 'sort:nullslast',
        },
      })
      .pipe(map(response => response.categories));
  }

  getRequestFileCategoryGroups$(
    counterpartyIds: Counterparty['id'][],
  ): Observable<RequestFileCategoryGroup[]> {
    return this.httpClient
      .get<{
        groups: RequestFileCategoryGroup[];
      }>(`${api2Url}/logistic/requests/fileCategoryGroups`, {
        params: {
          counterpartyId: counterpartyIds,
        },
      })
      .pipe(map(response => response.groups));
  }

  getRequestsMainFiles(
    requestsIds: RequestDTO['id'][],
  ): Observable<MainRequestFilesPayload> {
    return this.httpClient.get<MainRequestFilesPayload>(
      `${api2Url}/logistic/request/files/mainFiles?requestsIds=${requestsIds.join(',')}`,
    );
  }

  downloadDenyAct$(
    requestId: RequestDTO['id'],
    actType: DenyActTypeEnum,
  ): Observable<ArrayBuffer> {
    return this.httpClient.get(
      `${apiUrl}/logistic/request/${requestId}/generateAlfaDenyAct/${actType}`,
      { responseType: 'arraybuffer' },
    );
  }

  uploadRequestFile$(
    requestId: RequestDTO['id'],
    requestNumber: RequestDTO['requestNumber'],
    fileCat: RequestFileCategory,
    file: File,
  ): Observable<UploadProgress<string>> {
    const formData = new FormData();
    const catId = fileCat.id;
    const fileDir = `/docs/requests/${requestId}/${catId}/`;
    let fileUploadUrl = `${api2Url}/logistic/request/files?dir=${fileDir}`;

    formData.append('file', file, file.name);

    if (fileCat.fileRenamePrefix) {
      fileUploadUrl +=
        '&fileRenamePrefix=' + fileCat.fileRenamePrefix + '_' + requestNumber;
    }

    return this.httpClient
      .post<string>(fileUploadUrl, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              return {
                progress: Math.round((100 * event.loaded) / (event.total ?? 1)),
              };
            case HttpEventType.Response:
              return { progress: 100, response: event.body };
            default:
              return { progress: 0 };
          }
        }),
      );
  }

  saveRequestFileName$(
    requestId: RequestDTO['id'],
    catId: RequestFileCategory['id'],
    filename: string,
    originFilename: string,
  ): Observable<RequestFile> {
    const path = `/docs/requests/${requestId}/${catId}/${filename}`;

    return this.httpClient.post<RequestFile>(
      `${api2Url}/logistic/request/files/${requestId}/${catId}`,
      {
        path,
        filename,
        originFilename,
      },
    );
  }

  deleteRequestFile$(file: RequestFile): Observable<void> {
    return this.httpClient.post<void>(
      `${api2Url}/logistic/request/removefile`,
      {
        file,
      },
    );
  }

  createFileDeleteRequest$(
    requestId: RequestDTO['id'],
    file: RequestFile,
  ): Observable<void> {
    const isDeleted = file.deleteRequestUserId !== null;

    return this.httpClient.put<void>(
      `${api2Url}/logistic/request/${requestId}/files/${file.id}/setDelete`,
      { isDeleted: !isDeleted },
    );
  }

  getFileCatGroupDownloadLink(
    requestId: RequestDTO['id'],
    categoryGroupId: RequestFileCategoryGroup['id'],
  ) {
    return `${apiUrl}/logistic/request/${requestId}/filesCatGroups/${categoryGroupId}`;
  }

  loadTemplatesOfActs$(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<TemplatesOfActsPayload> {
    return this.httpClient.get<TemplatesOfActsPayload>(
      `${api2Url}/logistic/settings/templatesOfActs/findByRequestNumber/${requestNumber}`,
    );
  }

  openRequestPdf$(requestId: RequestDTO['id']) {
    return this.httpClient.get(`${apiUrl}/logistic/request/${requestId}/pdf`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  generateAct$(requestNumber: RequestDTO['requestNumber'], templateId: number) {
    return this.httpClient.get(
      `${apiUrl}/logistic/settings/templatesOfActs/${templateId}/generateByRequestNumber/${requestNumber}`,
      { responseType: 'blob', observe: 'response' },
    );
  }

  generateAllActs$(requestNumber: RequestDTO['requestNumber']) {
    return this.httpClient.get(
      `${apiUrl}/logistic/settings/templatesOfActs/all/generateByRequestNumber/${requestNumber}`,
      { responseType: 'blob', observe: 'response' },
    );
  }

  sendFilesByEmail$(
    requestId: RequestDTO['id'],
    address: string,
    fileGroupsWithFiles: FileGroupsWithFilesToSend,
  ) {
    return this.httpClient.post<void>(
      `${apiUrl}/logistic/request/${requestId}/sendByEmail`,
      { address, fileGroupsWithFiles },
    );
  }

  // #endregion files

  getRequestComments$(
    requestId: RequestDTO['id'],
  ): Observable<RequestComment[]> {
    return this.httpClient.get<RequestComment[]>(
      `${api2Url}/logistic/request/${requestId}/comments`,
    );
  }

  // #region products

  getAvailableProducts$(
    params: AvailableProductsQueryParams = {},
  ): Observable<Product[]> {
    const queryParams: Record<string, string | number | boolean> = {};

    if (params.forRequestId !== undefined) {
      queryParams['forRequestId'] = params.forRequestId;
    }
    if (params.forCounterpartyId !== undefined) {
      queryParams['forCounterpartyId'] = params.forCounterpartyId;
    }
    if (params.isPrivate !== undefined) {
      queryParams['isPrivate'] = params.isPrivate;
    }
    if (params.scopeToVisibleRequests !== undefined) {
      queryParams['scopeToVisibleRequests'] = params.scopeToVisibleRequests;
    }
    if (params.requestType !== undefined) {
      queryParams['requestType'] = params.requestType;
    }
    if (params.requestTypes?.length) {
      queryParams['requestTypes'] = JSON.stringify(params.requestTypes);
    }

    return this.httpClient.get<Product[]>(
      `${api2Url}/logistic/availableProducts`,
      { params: queryParams },
    );
  }

  addProductToRequest$(productData: {
    requestId: RequestDTO['id'];
    requestProductId: Product['id'];
    amount: RequestProduct['amount'];
  }): Observable<void> {
    return this.httpClient.post<void>(
      `${apiUrl}/logistic/requestProducts/`,
      productData,
    );
  }

  removeProductFromRequest$(
    requestProductId: RequestProduct['id'],
  ): Observable<void> {
    return this.httpClient.delete<void>(
      `${apiUrl}/logistic/requestProducts/${requestProductId}`,
    );
  }

  getRequiredFieldsSettingsForRequest$(
    requestId: RequestDTO['id'],
  ): Observable<RequestRequiredFieldsSettings> {
    return this.httpClient.get<RequestRequiredFieldsSettings>(
      `${api2Url}/logistic/required-fields/settings/${requestId}`,
    );
  }

  changeProductCompletionStatus$(
    requestProductId: RequestProduct['id'],
    newCompletionStatus: boolean,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/requestProducts/${requestProductId}/completed`,
      { completed: newCompletionStatus },
    );
  }

  cancelRequestProductForcedClosure$(
    requestProductId: RequestProduct['id'],
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/requestProducts/cancelForcedClosure`,
      { id: requestProductId },
      { responseType: 'text' as any },
    );
  }

  validateRequiredFields$(
    requestId: RequestDTO['id'],
    productId: Product['id'],
  ): Observable<{ isSuccess: boolean; helpText: string }> {
    return this.httpClient
      .get<string>(`${api2Url}/logistic/required-fields/validate`, {
        params: {
          requestId,
          productId,
        },
        responseType: 'text' as any,
      })
      .pipe(
        map(successText => ({ isSuccess: true, helpText: successText })),
        catchError((err: HttpErrorResponse) => {
          if (err.status === HttpStatusCode.ExpectationFailed) {
            return of({ isSuccess: false, helpText: err.error });
          } else {
            return throwError(() => err);
          }
        }),
      );
  }

  setFailedAttemptToCompleteProduct$(
    requestProductId: RequestProduct['id'],
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/requestProducts/setFailedAttemptToCompleteProduct`,
      { id: requestProductId },
      { responseType: 'text' as any },
    );
  }

  closeProductForsibly$(
    requestProductId: RequestProduct['id'],
    reason: string,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/requestProducts/closeProductForcingly`,
      { id: requestProductId, reason },
      { responseType: 'text' as any },
    );
  }

  changeProductAmount$(
    requestProductId: RequestProduct['id'],
    newAmount: number,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/requestProducts/${requestProductId}/chaneAmount`,
      { amount: newAmount },
    );
  }

  // #endregion products

  // #region devices

  getDevicesByRequestNumber$(
    requestNumber: RequestDTO['requestNumber'],
  ): Observable<RequestDevice[]> {
    return this.httpClient.get<RequestDevice[]>(
      `${api2Url}/logistic/request/${requestNumber}/devices`,
    );
  }

  addDeviceToRequest$(
    requestId: RequestDTO['id'],
    deviceData: Pick<
      RequestDevice,
      | 'name'
      | 'installDate'
      | 'secondSim'
      | 'hasProtectCase'
      | 'protectCaseNumber'
      | 'onboardVoltage'
      | 'mountType'
      | 'cutoffSwitch'
    >,
  ): Observable<RequestDevice> {
    return this.httpClient.post<RequestDevice>(
      `${apiUrl}/logistic/request/${requestId}/devices`,
      deviceData,
      { responseType: 'text' as any },
    );
  }

  getDeviceInstallQualityStatus$(
    deviceHash: RequestDevice['deviceHash'],
  ): Observable<DeviceInstallQualityCheckStatus> {
    return this.httpClient.get<DeviceInstallQualityCheckStatus>(
      `${apiUrl}/logistic/request/devices/${deviceHash}/installQuality/status`,
    );
  }

  getDeviceTamperStatus$(
    deviceHash: RequestDevice['deviceHash'],
  ): Observable<DeviceTamperStatus> {
    return this.httpClient.get<DeviceTamperStatus>(
      `${apiUrl}/logistic/request/devices/${deviceHash}/tamper/status`,
    );
  }

  scheduleDeviceInstallQualityRequest$(
    requestId: RequestDTO['id'],
    deviceHash: RequestDevice['deviceHash'],
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${apiUrl}/logistic/request/devices/${deviceHash}/installQuality/schedule/${requestId}`,
      undefined,
    );
  }

  updateLocationForTracker$(
    deviceHash: RequestDevice['deviceHash'],
    location: {
      latitude: string;
      longitude: string;
    },
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${apiUrl}/logistic/tracker/location?deviceHash=${deviceHash}`,
      {
        DeviceHash: deviceHash,
        Location: {
          Latitude: location.latitude,
          Longitude: location.longitude,
        },
      },
    );
  }

  updateRequestDevice$(
    requestDeviceId: RequestDevice['id'],
    deviceData: Pick<
      RequestDevice,
      | 'name'
      | 'secondSim'
      | 'hasProtectCase'
      | 'protectCaseNumber'
      | 'onboardVoltage'
      | 'mountType'
      | 'cutoffSwitch'
    >,
  ): Observable<RequestDevice> {
    return this.httpClient.put<RequestDevice>(
      `${apiUrl}/logistic/request/devices/${requestDeviceId}`,
      deviceData,
      {
        responseType: 'text' as any,
      },
    );
  }

  removeRequestDevice$(requestDeviceId: RequestDevice['id']): Observable<void> {
    return this.httpClient.delete<void>(
      `${apiUrl}/logistic/request/devices/${requestDeviceId}`,
      {
        responseType: 'text' as any,
      },
    );
  }

  getDeviceModelId$(
    sim1: RequestDevice['name'],
  ): Observable<RequestDeviceModelResponse> {
    return this.httpClient.get<RequestDeviceModelResponse>(
      `${apiUrl}/logistic/getDeviceModelId/${sim1}`,
    );
  }

  // #endregion devices

  // #region request settings

  getCheckList$(
    counterpartyId: number,
  ): Observable<CounterpartiesChecklistsCaption | object> {
    return this.httpClient.get<any>(
      `${apiUrl}/logistic/settings/counterpartiesChecklists/${counterpartyId}`,
    );
  }

  // #endregion request settings

  // #region ratings

  getRequestRatingsForInstaller$(): Observable<LogisticRequestRatingList[]> {
    return this.httpClient.get<LogisticRequestRatingList[]>(
      `${api2Url}/logistic/ratinglist/1`,
    );
  }

  toggleRequestRating$(
    requestId: RequestDTO['id'],
    ratingId: LogisticRequestRatingList['id'],
    action: LogisticRequestRatingActionEnum,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/request/${requestId}/rating/${ratingId}/${action}`,
      {},
      { responseType: 'text' as any },
    );
  }

  updateRequestRatingQCComment$(
    requestId: RequestDTO['id'],
    rating: LogisticRequestRatingList,
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${apiUrl}/logistic/request/${requestId}/rating/${rating.uniqueKey}`,
      {
        action: LogisticRequestRatingActionEnum.Comment,
        rating,
      },
      { responseType: 'text' as any },
    );
  }

  getQCRatings$(): Observable<LogisticRequestRatingList[]> {
    return this.httpClient.get<LogisticRequestRatingList[]>(
      `${api2Url}/logistic/ratinglist/2`,
    );
  }

  // #endregion ratings

  submitInstallerBrowserCoords$(
    requestId: RequestDTO['id'],
    position: GeolocationPosition,
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${apiUrl}/logistic/request/${requestId}/coords`,
      {
        lat: position.coords.latitude,
        lon: position.coords.longitude,
      },
    );
  }

  getRequestChangeHistory$(
    requestId: RequestDTO['id'],
  ): Observable<RequestLog[]> {
    return this.httpClient.get<RequestLog[]>(
      `${apiUrl}/logistic/logs/request/${requestId}`,
    );
  }

  compareRequestHistoryLogs$(
    comparingLogs: ComparingLogs,
    changeAlgorithm?: boolean,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${apiUrl}/logistic/logs/compare`,
      {
        ...comparingLogs,
      },
      {
        responseType: 'text' as any,
        params: changeAlgorithm ? { cleanup: true } : {},
      },
    );
  }

  createRequests$(
    payload: CreateRequestsBatchPayload,
  ): Observable<CreatedRequestResult[]> {
    return this.httpClient.post<CreatedRequestResult[]>(
      `${api2Url}/logistic/request/batch`,
      payload,
    );
  }

  sendRequestToWork$(requestId: number): Observable<void> {
    return this.httpClient.put<void>(
      `${api2Url}/logistic/request/${requestId}/sendToWork`,
      {},
    );
  }
}
